
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import {
  defineComponent, onMounted, Ref, ref, SetupContext, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { CampaignsBulkStatusEnum } from '@/types';
import EditorModal from '@/components/modals/EditorModal.vue';
import Loader from '@/components/layout/Loader.vue';
import { asInt, showToastError, showToastSuccess } from '@/helpers';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { getNowFormattedByTimezone } from '@/helpers/Date';
import { openBulkCampaignEditor } from '@/views/workflows/BulkCampaignsActions/useBulkCampaignsActions';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import { store } from '@/store';
import { getCampaignsBulkBaseList } from '@/composables/workflows/CampaignBulk';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import RequireIntegrationDialog from '@/components/integrations/RequireIntegrationDialog.vue';
import type { IntegrationOwnedItem } from '@/types/integrations';
import { useIntegrationGating } from '@/composables/integrations/useIntegrationGating';
import { useIntegrationCollections } from '@/composables/integrations/useIntegrationCollections';
import DrilldownBanner from '@/components/integrations/DrilldownBanner.vue';
import { pickLocalizedText } from '@/utils/localized-text';
import IntegrationCollectionCard from '@/components/integrations/IntegrationCollectionCard.vue';

interface CampaignsBulkBase extends IntegrationOwnedItem {
  id: number;
  name: Record<string, string>;
  legend: Record<string, string>;
  tags: string[];
  categories: string;
  path: string;
  images_templates: string[] | null;
}

const CHANNEL_TAGS = ['Email', 'SMS', 'Push', 'WhatsApp', 'Popup', 'Cross-canal'];

export default defineComponent({
  name: 'CampaignsBulkBaseSelectorV2',

  components: {
    TemplateHtmlPreviewSlider,
    Loader,
    EditorModal,
    Button,
    MultiSelect,
    RequireIntegrationDialog,
    IntegrationCollectionCard,
    DrilldownBanner,
  },

  emits: {
    'on-close-dialog': null,
  },

  setup(props: { }, context: SetupContext) {
    const { t, locale } = useI18n();

    const currentLocale = computed(() => locale.value || 'fr');

    const getLocalizedText = (textObj: Record<string, string> | undefined, fallback = ''): string => {
      if (!textObj || typeof textObj !== 'object') return fallback;
      return pickLocalizedText(textObj, currentLocale.value) || fallback;
    };

    const isListLoading = ref(false);
    const list: Ref<CampaignsBulkBase[]> = ref([]);
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);
    const searchQuery = ref('');

    // Integration gating shared with the automation/template pickers via
    // `useIntegrationGating`.
    const selectedAuthors = ref<string[]>([]);
    const {
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      gatePreflight,
      gateBackendResponse,
      authorFilterOptionsFor,
    } = useIntegrationGating();

    const authorFilterOptions = authorFilterOptionsFor(() => list.value);

    // ----- Visual author filter (same UX as the automation picker) -----

    const authorSearchQuery = ref('');

    /** All author options (ShopiMind + integrations at the same level),
     *  filtered by the search input. Integration options carry a `count`. */
    const filteredAuthorOptions = computed(() => {
      const q = authorSearchQuery.value.trim().toLowerCase();
      if (!q) return authorFilterOptions.value;
      return authorFilterOptions.value.filter(
        (o) => o.label.toLowerCase().includes(q)
          || o.value.toLowerCase().includes(q),
      );
    });

    const toggleAuthor = (value: string) => {
      const idx = selectedAuthors.value.indexOf(value);
      if (idx >= 0) {
        selectedAuthors.value.splice(idx, 1);
      } else {
        selectedAuthors.value.push(value);
      }
    };

    const getAuthorIcon = (slug: string): string | undefined => {
      if (slug === 'shopimind') return undefined;
      const match = list.value.find((item) => item.integration_slug === slug);
      return match?.integration_icon_url ?? undefined;
    };

    // Tag filter
    const selectedTags = ref<string[]>([]);

    const channelTagGroup = computed(() => {
      const selected = selectedTags.value;
      const tagCounts = new Map<string, number>();

      for (const item of list.value) {
        const itemTags = item.tags || [];
        if (!selected.every((t) => itemTags.includes(t))) continue;
        for (const tag of itemTags) {
          if (CHANNEL_TAGS.includes(tag)) {
            tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
          }
        }
      }

      const tags: { value: string; count: number }[] = [];
      for (const [value, count] of tagCounts) {
        tags.push({ value, count });
      }
      // Sort by CHANNEL_TAGS order
      tags.sort((a, b) => CHANNEL_TAGS.indexOf(a.value) - CHANNEL_TAGS.indexOf(b.value));

      return tags;
    });

    const toggleTag = (tag: string) => {
      const idx = selectedTags.value.indexOf(tag);
      if (idx >= 0) {
        selectedTags.value.splice(idx, 1);
      } else {
        selectedTags.value.push(tag);
      }
    };

    const filteredList = computed(() => {
      let result = list.value;

      // Filter by selected tags (AND: all selected tags must be present)
      if (selectedTags.value.length > 0) {
        result = result.filter((campaign) => {
          const tags = campaign.tags || [];
          return selectedTags.value.every((tag) => tags.includes(tag));
        });
      }

      // Filter by search query
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((campaign) => getLocalizedText(campaign.name).toLowerCase().includes(query)
            || getLocalizedText(campaign.legend).toLowerCase().includes(query));
      }

      // Filter by author. Empty selection = no constraint. Otherwise we
      // keep items whose author key (slug or 'shopimind') is in the set.
      if (selectedAuthors.value.length > 0) {
        result = result.filter((campaign) => {
          const authorKey = campaign.author === 'integration'
            ? campaign.integration_slug ?? ''
            : 'shopimind';
          return selectedAuthors.value.includes(authorKey);
        });
      }

      return result;
    });

    const closeModal = () => {
      context.emit('on-close-dialog');
    };

    const handleStartFromScratch = async () => {
      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        // Generate campaign name with current date
        const name = `${t('campaignPrefix')} - ${getNowFormattedByTimezone()}`;

        // Create empty bulk campaign via API
        const result = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/campaigns/bulk',
          {},
          '',
          { name },
        );

        if (result && result.data && result.data.id) {
          // Use shared function to open the editor with all checks
          await openBulkCampaignEditor(
            result.data.id,
            result.data.status as CampaignsBulkStatusEnum,
            result.data.name,
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Campaign creation error:', error);
        showToastError(t('campaignBulk.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    const handleChoose = async (record: CampaignsBulkBase) => {
      // Pre-flight: bail out if the integration isn't active on this shop.
      if (gatePreflight(record)) return;

      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/create-from-base',
          {},
          '',
          {
            idShop,
            type: 'campaign_bulk',
            baseId: record.path,
          },
        );

        // Backend safety net (deactivation between page load and click)
        if (gateBackendResponse(result, record)) {
          store.commit('general/hideTheSpinner');
          return;
        }

        if (result && result.success && result.id) {
          showToastSuccess(t('campaignBulk.create_action.success'));
          setRefreshScenariosList(true);
          await openBulkCampaignEditor(
            asInt(result.id),
            CampaignsBulkStatusEnum.DRAFT,
            result.name || '',
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Workflow creation error:', error);
        showToastError(t('campaignBulk.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    const openGalleryHtmlPreview = async (record: CampaignsBulkBase) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/gallery-html-preview',
          {},
          '',
          { path: record.path },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(t('campaignBulk.workflowSelector.preview.error'));
        }
      } catch {
        showToastError(t('campaignBulk.workflowSelector.preview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    onMounted(async () => {
      isListLoading.value = true;

      try {
        const idShop = UserState.activeShop?.id ?? undefined;
        const result: CampaignsBulkBase[] = await getCampaignsBulkBaseList(idShop);

        if (result) {
          list.value = result;
        }
      } catch (error) {
        showToastError(t('campaignBulk.list.error'));
      } finally {
        isListLoading.value = false;
      }
    });

    // Collapse an integration's many predefined items into one collection card
    // (anti-flood). Clicking a collection drills into that integration.
    const { displayItems, drilledInIntegration } = useIntegrationCollections(
      () => filteredList.value,
      selectedAuthors,
    );
    const drillIntoIntegration = (slug: string) => {
      selectedAuthors.value = [slug];
    };
    const clearAuthorFilter = () => {
      selectedAuthors.value = [];
    };

    return {
      t,
      filteredList,
      displayItems,
      drilledInIntegration,
      drillIntoIntegration,
      clearAuthorFilter,
      htmlPreviewData,
      isListLoading,
      searchQuery,
      closeModal,
      handleChoose,
      openGalleryHtmlPreview,
      closeHtmlPreview,
      handleStartFromScratch,
      selectedAuthors,
      authorFilterOptions,
      filteredAuthorOptions,
      authorSearchQuery,
      toggleAuthor,
      getAuthorIcon,
      selectedTags,
      channelTagGroup,
      toggleTag,
      getLocalizedText,
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
    };
  },
});
